import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "../components/sub-comp/Navbar";
import Footer from "../components/sub-comp/MainFooter";
import FloatingSocialPanels from "../components/sub-comp/FloatingSocialPanels";
import { getEventById, getEventsList } from "../data/events";
import cmsApi from "../services/cmsApi";
import { getImageUrl, FALLBACK_IMAGE_SVG } from "../utils/getImageUrl";

const EventDetail = () => {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [allEvents, setAllEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (typeof window === 'undefined' || !id) return;

    const fetchEvent = async () => {
      try {
        setLoading(true);
        let foundEvent = null;

        // Step 1: Try to fetch from CMS API by slug or id
        try {
          const result = await cmsApi.getEvents({ limit: 100 });
          if (result.success && result.events && Array.isArray(result.events)) {
            // Try to find by slug first, then by id
            foundEvent = result.events.find(e => 
              e.slug === id || e.id === id || String(e.id) === String(id)
            );

            if (foundEvent) {
              // Set all events for related events
              setAllEvents(result.events.map(e => ({
                id: e.id,
                slug: e.slug,
                title: e.title,
                description: e.description || '',
                image: e.image || '',
                date: e.date || '',
                dateISO: e.dateISO || '',
                category: e.category || '',
                location: e.location || '',
                time: e.time || '',
                fullDescription: e.content || e.description || ''
              })));

              // Format the found event
              setEvent({
                id: foundEvent.id,
                slug: foundEvent.slug,
                title: foundEvent.title,
                description: foundEvent.description || '',
                image: foundEvent.image || '',
                date: foundEvent.date || '',
                dateISO: foundEvent.dateISO || '',
                category: foundEvent.category || '',
                location: foundEvent.location || '',
                time: foundEvent.time || '',
                fullDescription: foundEvent.content || foundEvent.description || ''
              });
              setLoading(false);
              return;
            }
          }
        } catch (apiError) {
          console.warn('CMS Events API failed:', apiError);
        }

        // Step 2: Fallback to hardcoded data
        const hardcodedEvent = getEventById(id);
        if (hardcodedEvent) {
          setEvent(hardcodedEvent);
          setAllEvents(getEventsList());
        }
      } catch (error) {
        console.error('Error fetching event:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [id]);

  useEffect(() => {
    if (!event) return;
    const prev = document.title;
    document.title = `${event.title} | JRIIT Events`;
    return () => { document.title = prev; };
  }, [event]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="mid-desktop-page">
        <div className="container mx-auto px-4 py-20 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-custom-pink border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading event...</p>
        </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="mid-desktop-page">
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">Event Not Found</h1>
          <Link to="/events" className="inline-block px-6 py-3 rounded-full bg-custom-pink text-white hover:bg-purple-700 transition">Back to Events</Link>
        </div>
        </div>
        <Footer />
      </div>
    );
  }

  const related = allEvents.filter((e) => 
    e.category === event.category && 
    (e.id !== event.id && e.slug !== event.slug)
  ).slice(0, 3);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="mid-desktop-page">

      <section className="relative py-16 bg-gradient-to-r from-custom-pink to-purple-800 text-white">
        <div className="container mx-auto px-4">
          <nav className="text-white/80 text-sm mb-4">
            <Link to="/" className="hover:text-white">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/events" className="hover:text-white">Events</Link>
            <span className="mx-2">/</span>
            <span className="text-white">{event.title}</span>
          </nav>
          <h1 className="text-3xl md:text-5xl font-bold mb-2">{event.title}</h1>
          <div className="flex flex-wrap gap-3 items-center text-white/90">
            <span className="px-3 py-1 bg-gold text-black rounded-full font-bold text-sm">{event.category}</span>
            <span className="text-sm">{event.date}</span>
            {event.time && <span className="text-sm">{event.time}</span>}
            {event.location && <span className="text-sm">{event.location}</span>}
          </div>
        </div>
      </section>

      <section className="py-8 bg-white">
        <div className="container mx-auto px-4">
          <img 
            src={getImageUrl(event.image)} 
            alt={event.title} 
            className="w-full h-[300px] md:h-[480px] object-cover rounded-xl shadow"
            onError={(e) => {
              e.currentTarget.src = FALLBACK_IMAGE_SVG;
            }}
          />
        </div>
      </section>

      <section className="py-12">
        <div className="container mx-auto px-4 grid grid-cols-1 xl:grid-cols-4 gap-8">
          <article className="xl:col-span-3 bg-white rounded-xl shadow p-6">
            <div className="text-gray-800 leading-relaxed whitespace-pre-line prose max-w-none">
              {event.fullDescription ? (
                <div dangerouslySetInnerHTML={{ 
                  __html: (event.fullDescription || event.description)
                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                    .replace(/\n/g, '<br/>')
                }} />
              ) : (
                <p>{event.description}</p>
              )}
            </div>
            <div className="mt-8 flex flex-wrap gap-3 items-center">
              <Link to="/events" className="px-4 py-2 rounded-full bg-gold text-black hover:bg-yellow-400 transition">Back to Events</Link>
            </div>
          </article>
          <aside className="xl:col-span-1">
            <div className="bg-white rounded-xl shadow p-5">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Related Events</h3>
              <div className="space-y-4">
                {related.map((e) => (
                  <Link key={e.id || e.slug} to={`/events/${e.slug || e.id}`} className="flex gap-3 group">
                    <img 
                      src={getImageUrl(e.image)} 
                      alt={e.title} 
                      className="w-16 h-16 object-cover rounded"
                      onError={(e) => {
                        e.currentTarget.src = FALLBACK_IMAGE_SVG;
                      }}
                    />
                    <div>
                      <p className="text-sm font-semibold group-hover:text-custom-pink">{e.title}</p>
                      <p className="text-xs text-gray-500">{e.date}</p>
                    </div>
                  </Link>
                ))}
                {related.length === 0 && (
                  <p className="text-gray-600 text-sm">No related events.</p>
                )}
              </div>
            </div>
          </aside>
        </div>
      </section>

      </div>

      <Footer />
      <FloatingSocialPanels />
    </div>
  );
};

export default EventDetail;
